import React, { useState, useEffect } from "react";
import { Box, TextField, Button, Typography, Container, Paper, Alert } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { API_BASE_URL } from "../config/apiKey.js";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("token");
    if (t) {
      setToken(t);
    } else {
      setError("Invalid or missing reset token.");
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!token) {
      setError("Invalid or missing reset token.");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess("Password updated successfully. You can now log in.");
        setTimeout(() => navigate("/login"), 2000);
      } else {
        setError(data.error || "Failed to reset password");
      }
    } catch (e) {
      setError(e.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top, #1e3a8a 0%, #020617 45%, #000814 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(rgba(56, 189, 248, 0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(37, 99, 235, 0.08) 1px, transparent 1px)
          `,
          backgroundSize: "50px 50px",
          opacity: 0.3,
        }}
      />

      <Container maxWidth="sm">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Paper
            elevation={24}
            sx={{
              p: 4,
              bgcolor: "rgba(15, 23, 42, 0.96)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(37, 99, 235, 0.4)",
              borderRadius: 4,
            }}
          >
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography variant="h4" sx={{ color: "white", fontWeight: "bold", mb: 1 }}>
                Reset Password
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(148,163,184,0.9)" }}>
                Enter your new password below.
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, bgcolor: "rgba(211, 47, 47, 0.2)", color: "white" }}>
                {(() => {
                  if (typeof error === "string") return error;
                  if (error?.message && typeof error.message === "string") return error.message;
                  if (error?.error && typeof error.error === "string") return error.error;
                  try {
                    return JSON.stringify(error);
                  } catch {
                    return "An error occurred";
                  }
                })()}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 3, bgcolor: "rgba(34, 197, 94, 0.2)", color: "white" }}>
                {(() => {
                  if (typeof success === "string") return success;
                  if (success?.message && typeof success.message === "string") return success.message;
                  return String(success);
                })()}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                sx={{ mb: 3 }}
              />
              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  bgcolor: "linear-gradient(135deg, #2563eb 0%, #38bdf8 100%)",
                  color: "white",
                  py: 1.5,
                  fontSize: "16px",
                  textTransform: "none",
                  borderRadius: 999,
                  "&:hover": {
                    bgcolor: "linear-gradient(135deg, #1d4ed8 0%, #0ea5e9 100%)",
                  },
                }}
              >
                {loading ? "Updating..." : "Update Password"}
              </Button>
            </form>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
}









